import { useState } from 'react'

import { useMediaQuery } from '@sparkai/hooks'
import { cn, type AppTimezonePreference } from '@sparkai/utils'

import { Avatar } from '../../Avatar'
import { Badge } from '../../Badge'
import { Dot } from '../../Dot'
import { Button } from '../../General/Button'
import { Icon, IconsName } from '../../Icon'
import { AvailableAvatarIcons, IconAvatar, IconAvatarProps } from '../../IconAvatar'
import { TimeAgo } from '../../TimeAgo'

function getIconSize(size: 'small' | 'large', isMobile: boolean, fileVariant: boolean) {
  if (size === 'small') return 20
  if (fileVariant) return isMobile ? 32 : 48
  return isMobile ? 20 : 24
}

function getImageSize(size: 'small' | 'large', isMobile: boolean) {
  if (isMobile) return 32
  if (size === 'small') return 20
  return 48
}

type NotificationAvatar =
  | {
      type: 'icon'
      icon: IconAvatarProps
      color?: string
      addon?: React.ReactNode
    }
  | {
      type: 'image'
      src: string
      title: string
      addon?: React.ReactNode
    }

export type NotificationItemProps = BaseNotificationItemProps & {
  title: string
  descriptionAddon?: React.ReactNode
}

export type BaseNotificationItemProps = {
  avatar: NotificationAvatar
  subTitle?: string
  size: 'small' | 'large'
  time: number
  tag?: string
  read?: boolean
  id: string
  timezonePreference?: AppTimezonePreference
  onRowClick?: (id: string) => void
  onMarkAsRead?: (id: string) => void
}

export function NotificationItem({
  avatar,
  title,
  subTitle,
  size,
  time,
  tag,
  read,
  id,
  timezonePreference,
  onRowClick,
  onMarkAsRead,
  descriptionAddon,
}: NotificationItemProps) {
  const { isMobile } = useMediaQuery()

  const [isHover, setIsHover] = useState(false)

  const shouldShowMarkAsRead = !read && isHover && size === 'large' && !isMobile

  return (
    <button
      className={cn(
        'NotificationItem lg:max-w-auto flex min-h-11 w-full items-center gap-2 hover:bg-blue-50 lg:min-w-[420px]',
        {
          'max-w-[420px] p-3': size === 'small',
          'px-2 py-4 lg:px-2 lg:py-2': size === 'large',
        }
      )}
      onClick={(e) => {
        onRowClick?.(id)
        e.preventDefault()
        e.stopPropagation()
      }}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      {!read && (
        <div className='relative mr-2 flex items-center'>
          <Dot data-testid='notification-item-dot' className={'bg-red-400'} />
        </div>
      )}

      {avatar.type === 'icon' && (
        <div
          className={cn('relative rounded-full bg-white', {
            'border border-gray-200 p-[5px] lg:p-[11px]': size === 'large' && avatar.icon.type === 'library',
          })}
        >
          {avatar.icon.type === 'library' && (
            <IconAvatar
              icon={avatar.icon.icon as IconsName}
              className='min-h-5 min-w-5 p-0'
              size={getIconSize(size, isMobile, false)}
              color={avatar.color}
              iconColor={avatar.icon.color}
              data-testid='notification-item-icon-avatar'
              type={'library'}
              iconProps={{
                className: cn('lg:w-auto lg:h-auto w-4 h-4'),
              }}
              fallbackText={avatar.icon.fallbackText ?? ''}
            />
          )}

          {avatar.icon.type === 'file' && (
            <IconAvatar
              icon={avatar.icon.icon as AvailableAvatarIcons}
              className='min-h-5 min-w-5 p-0'
              size={getIconSize(size, isMobile, true)}
              color={avatar.color}
              iconColor={avatar.icon.color}
              data-testid='notification-item-icon-avatar'
              type={'file'}
              iconProps={{
                className: cn('lg:w-auto lg:h-auto'),
              }}
              fallbackText={avatar.icon.fallbackText ?? ''}
            />
          )}
          {avatar.addon}
        </div>
      )}

      {avatar.type === 'image' && (
        <div className='relative'>
          <Avatar
            fallback={{ value: avatar.title }}
            fallbackProps={{ className: size === 'small' ? 'text-xs' : undefined }}
            imageProps={{ src: avatar.src }}
            width={getImageSize(size, isMobile)}
            height={getImageSize(size, isMobile)}
          />
          {avatar.addon}
        </div>
      )}

      <div className='flex flex-1 flex-col gap-1 lg:flex-row lg:gap-2'>
        <div className={cn('flex w-full flex-1 items-center gap-1 text-left')}>
          <div className='flex flex-col gap-1'>
            <div className='flex w-full flex-col gap-1 lg:flex-row'>
              <p className={cn('line-clamp-1 text-sm font-medium text-gray-900')}>{title}</p>
              {subTitle && <p className='line-clamp-1 flex-grow text-sm text-gray-500'>{subTitle}</p>}
            </div>
            {size === 'large' && descriptionAddon}
          </div>
        </div>
        <span className='flex min-w-fit flex-col items-start gap-1 text-xs font-medium text-gray-500 lg:items-end'>
          {shouldShowMarkAsRead && (
            <Button
              type='button'
              className='flex min-w-[150px] justify-center gap-2'
              variant='white'
              size='xs'
              onClick={(e) => {
                onMarkAsRead?.(id)
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              <Icon name='check' />
              <p>Mark as read</p>
            </Button>
          )}

          {!shouldShowMarkAsRead && (
            <>
              <TimeAgo
                timestamp={time}
                isShort={size === 'small'}
                timezonePreference={timezonePreference}
                className='ml-[2px]'
              />
              {size === 'large' && tag && (
                <Badge className='hidden text-xs text-gray-900 lg:block'>{tag}</Badge>
              )}
            </>
          )}
        </span>
      </div>
    </button>
  )
}
